import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { formatDateTime } from "helpers/common";
import { api } from "helpers/axios";
import ErrorAlert from "components/ui/ErrorAlert";
import { useTranslation } from "translation/useTranslation";

const LICENSE_STATUS_CLASS = {
  valid: "badge badge-success",
  warning: "badge badge-warning",
  blocked: "badge badge-danger",
};

function normalizeLicenseState(payload) {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const health = String(payload.health || "").toLowerCase();
  const status = health || "unknown";

  return {
    status,
    mode: payload.mode || "connected",
    licenseKey: payload.license_key_masked || null,
    expirationDate: payload.expiration_date || null,
    restrictions: payload.restrictions || {},
    lastReason: payload.last_reason || null,
    lastCheckAt: payload.last_check_at || null,
    lastOnlineCheckAt: payload.last_online_check_at || null,
    releasedAt: payload.offline_stale_at || null,
  };
}

function normalizeSoftwareRelease(payload) {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  return {
    version: payload.version || "-",
    environment: payload.environment || "-",
    releasedAt: payload.released_at || null,
    internalNotes: payload.internal_notes || null,
  };
}

function parseInternalNoteComponent(raw, key) {
  if (!raw || typeof raw !== "string") {
    return null;
  }

  const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const pattern = new RegExp(`${escapedKey}\\s*=\\s*([^,]+)`, "i");
  const matched = raw.match(pattern);
  if (!matched?.[1]) {
    return null;
  }

  const segment = matched[1].trim();
  const pkgCommit = segment.match(/^([^@]+)@([a-f0-9]{7,40})/i);
  const refMatch = segment.match(/\(ref=([^)]+)\)/i);

  return {
    package: pkgCommit?.[1]?.trim() || null,
    commit: pkgCommit?.[2] || null,
    ref: refMatch?.[1]?.trim() || null,
  };
}

function formatComponentVersion(component) {
  if (!component) {
    return "-";
  }

  const shortCommit = component.commit ? component.commit.slice(0, 8) : null;

  if (component.ref && shortCommit) {
    return `${component.ref} (${shortCommit})`;
  }

  if (component.ref) {
    return component.ref;
  }

  if (shortCommit) {
    return shortCommit;
  }

  if (component.package) {
    return component.package;
  }

  return "-";
}

function licenseStatusLabel(status) {
  if (status === "valid") return "Valid";
  if (status === "warning") return "Warning";
  if (status === "blocked") return "Blocked";
  return "Unknown";
}

function parseExpiryDate(value) {
  if (!value || typeof value !== "string") {
    return null;
  }

  const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return new Date(Number(year), Number(month) - 1, Number(day), 23, 59, 59, 999);
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed;
}

function formatValidUntil(value) {
  const expiry = parseExpiryDate(value);
  if (!expiry) {
    return value || "-";
  }

  const formattedDate = expiry.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });

  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const expiryDay = new Date(expiry.getFullYear(), expiry.getMonth(), expiry.getDate());
  const diffDays = Math.floor((expiryDay.getTime() - todayStart.getTime()) / 86400000);

  let label = "";
  if (diffDays > 1) {
    label = `${diffDays} days left`;
  } else if (diffDays === 1) {
    label = "1 day left";
  } else if (diffDays === 0) {
    label = "expires today";
  } else if (diffDays === -1) {
    label = "expired 1 day ago";
  } else {
    label = `expired ${Math.abs(diffDays)} days ago`;
  }

  return `${formattedDate} (${label})`;
}

export default function About() {
  const { editableTranslate } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [licenseState, setLicenseState] = useState(null);
  const [softwareRelease, setSoftwareRelease] = useState(null);
  const [licenseError, setLicenseError] = useState(null);
  const [softwareError, setSoftwareError] = useState(null);

  useEffect(() => {
    let active = true;

    async function fetchAboutData() {
      setLoading(true);

      const [licenseResult, softwareResult] = await Promise.allSettled([
        api.get("/common/license/state"),
        api.get("/release-notes/latest"),
      ]);

      if (!active) {
        return;
      }

      if (licenseResult.status === "fulfilled") {
        const normalized = normalizeLicenseState(licenseResult.value?.data?.data);
        setLicenseState(normalized);
        setLicenseError(null);
      } else {
        setLicenseState(null);
        setLicenseError(licenseResult.reason || "Failed to load license state.");
      }

      if (softwareResult.status === "fulfilled") {
        const payload = softwareResult.value?.data?.data || softwareResult.value?.data;
        const normalized = normalizeSoftwareRelease(payload);
        setSoftwareRelease(normalized);
        setSoftwareError(null);
      } else {
        setSoftwareRelease(null);
        setSoftwareError(softwareResult.reason || "Failed to load software release.");
      }

      setLoading(false);
    }

    fetchAboutData();

    return () => {
      active = false;
    };
  }, []);

  const statusClass = useMemo(() => {
    const status = licenseState?.status || "unknown";
    return LICENSE_STATUS_CLASS[status] || "badge badge-secondary";
  }, [licenseState]);

  const modules = useMemo(() => {
    const value = licenseState?.restrictions?.modules;
    if (!Array.isArray(value) || value.length === 0) {
      return "-";
    }

    return value.join(", ");
  }, [licenseState]);

  const parsedInternalVersions = useMemo(() => {
    const internalNotes = softwareRelease?.internalNotes;

    return {
      frontend: parseInternalNoteComponent(internalNotes, "FE"),
      backend: parseInternalNoteComponent(internalNotes, "BE"),
    };
  }, [softwareRelease]);

  return (
    <div className="row">
      <div className="col d-flex">
        <div className="bg-white w-100 p-4 content-card min-height-after-navbar">
          <h4>{editableTranslate("global.about", "Über")}</h4>
          <p className="section-description">
            {editableTranslate("global.about_description", "Lizenzstatus und Software-Releaseinformationen.")}
          </p>

          <div className="row mt-4">
            <div className="col-lg-6 mb-4">
              <div className="border rounded p-3 h-100">
                <h4 className="mb-3">{editableTranslate("global.license", "Lizenz")}</h4>
                {licenseError && <ErrorAlert error={licenseError} onClose={() => setLicenseError(null)} />}

                {loading && !licenseState && !licenseError && (
                  <div className="text-muted">
                    <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true" />
                    {editableTranslate("global.loading", "Wird geladen...")}
                  </div>
                )}

                {licenseState && (
                  <div className="table-responsive">
                    <table className="table table-sm mb-0" data-testid="license-table">
                      <tbody>
                        <tr>
                          <th className="w-50">{editableTranslate("global.status", "Status")}</th>
                          <td data-testid="license-status"><span className={statusClass}>{licenseStatusLabel(licenseState.status)}</span></td>
                        </tr>
                        <tr>
                          <th>{editableTranslate("global.mode", "Verbindung")}</th>
                          <td data-testid="license-mode">{licenseState.mode || "-"}</td>
                        </tr>
                        <tr>
                          <th>{editableTranslate("global.license_key", "Lizenzschlüssel")}</th>
                          <td data-testid="license-key"><code>{licenseState.licenseKey || "-"}</code></td>
                        </tr>
                        <tr>
                          <th>{editableTranslate("global.valid_until", "Gültig bis")}</th>
                          <td data-testid="license-valid-until">{formatValidUntil(licenseState.expirationDate)}</td>
                        </tr>
                        <tr>
                          <th>{editableTranslate("field.module", "Module")}</th>
                          <td data-testid="license-modules">{modules}</td>
                        </tr>
                        <tr>
                          <th>{editableTranslate("global.max_employees", "Max. Anzahl der Datensätze")}</th>
                          <td data-testid="license-max-employees">{licenseState?.restrictions?.max_employees ?? "-"}</td>
                        </tr>
                        <tr>
                          <th>{editableTranslate("global.last_check", "Letzte Prüfung")}</th>
                          <td data-testid="license-last-check">{licenseState.lastCheckAt ? formatDateTime(licenseState.lastCheckAt) : "-"}</td>
                        </tr>
                        <tr>
                          <th>{editableTranslate("global.last_online_check", "Letzte online-Prüfung")}</th>
                          <td data-testid="license-last-online-check">{licenseState.lastOnlineCheckAt ? formatDateTime(licenseState.lastOnlineCheckAt) : "-"}</td>
                        </tr>
                        {
                          licenseState.status !== 'valid' ? (
                            <tr>
                              <th>{editableTranslate("global.warning", "Warnung")}</th>
                              <td data-testid="license-warning">{licenseState.lastReason || "-"}</td>
                            </tr>
                          ) : null
                        }
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            <div className="col-lg-6 mb-4">
              <div className="border rounded p-3 h-100">
                <h4 className="mb-3">{editableTranslate("global.software", "Software")}</h4>
                {softwareError && <ErrorAlert error={softwareError} onClose={() => setSoftwareError(null)} />}

                {loading && !softwareRelease && !softwareError && (
                  <div className="text-muted">
                    <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true" />
                    {editableTranslate("global.loading", "Wird geladen...")}
                  </div>
                )}

                {softwareRelease && (
                  <>
                    <div className="table-responsive">
                      <table className="table table-sm mb-3" data-testid="software-table">
                        <tbody>
                          <tr>
                            <th className="w-50">{editableTranslate("global.version", "Version")}</th>
                            <td data-testid="software-version"><code>{softwareRelease.version}</code></td>
                          </tr>
                          <tr>
                            <th>{editableTranslate("global.environment", "Umgebung")}</th>
                            <td data-testid="software-environment">{softwareRelease.environment}</td>
                          </tr>
                          <tr>
                            <th>{editableTranslate("global.fe_ref", "FE-Ref.")}</th>
                            <td data-testid="software-fe-ref">{formatComponentVersion(parsedInternalVersions.frontend)}</td>
                          </tr>
                          <tr>
                            <th>{editableTranslate("global.be_ref", "BE ref.")}</th>
                            <td data-testid="software-be-ref">{formatComponentVersion(parsedInternalVersions.backend)}</td>
                          </tr>
                          <tr>
                            <th>{editableTranslate("global.released_at", "Veröffentlicht")}</th>
                            <td data-testid="software-released-at">{softwareRelease.releasedAt ? formatDateTime(softwareRelease.releasedAt) : "-"}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
